import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import isEmpty from "lodash/isEmpty";
import * as Yup from "yup";

import MainLayout from "../../themes/layouts/Main";
import Form from "../../components/Projects/Create";
import Loader from "../../themes/components/Loader";
import { notify, notifySimple } from "../../themes/components/Notifications";
import { fetchSuppliers } from "../../api/Supplier";
import { createProject, getAllPms } from "../../api/Project";
import { AccountContext } from "../../contexts/CurrentUser";

const validationSchema = Yup.object().shape({
  input: Yup.object().shape({
    name: Yup.string().min(2, "Too short!").max(255, "Too long!").required("Name is required"),
    completed_responses_limit: Yup.number()
      .min(1, "Completed Responses limit should be atleast 1")
      .required("Completed Responses limit is required"),
    status: Yup.string().required("Status is required"),
    account_id: Yup.string().required("Project manager is required"),
    suppliers: Yup.array().of(
      Yup.object().shape({
        score: Yup.number()
          .min(20, "Score Threshold must be at least 20")
          .max(100, "Score Threshold must be at most 100")
          .required("Score Threshold is required"),
        rt_proof_of_human_threshold: Yup.number()
          .min(20, "Roundtable AI Proof of Human Score Threshold must be at least 20")
          .max(100, "Roundtable AI Proof of Human Score Threshold must be at most 100")
          .required("Roundtable AI Proof of Human Score Threshold is required"),
      })
    ),
  }),
});

const ProjectCreateRoute: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<never[]>([]);
  const [projectManagers, setProjectManagers] = useState<
    { id: string; first_name: string; last_name: string }[]
  >([]);
  const { account } = useContext(AccountContext);

  const fetchData = async () => {
    const result = await fetchSuppliers();
    setLoading(result.loading);
    setError(result.error);
    setData(result.data);

    // Fetch project managers
    const pmResponse = await getAllPms();
    setProjectManagers(Array.isArray(pmResponse.result) ? pmResponse.result : []);
  };

  const onSubmit = async (values: ProjectCreateVars) => {
    try {
      const response = await createProject(values);
      const { result, messages } = response.projectCreate;

      if (!isEmpty(messages)) {
        notify(messages, "error");
      } else if (result) {
        notifySimple(["Project created"], "success");
        navigate("/");
      }
    } catch (err) {
      notifySimple(["Error creating project"], "error");
    }
  };

  useEffect(() => {
    void fetchData();
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (error || !data) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <MainLayout>
      <Formik<ProjectCreateVars>
        onSubmit={onSubmit}
        initialValues={{
          input: {
            name: "",
            status: "PENDING",
            account_id: account?.id,
            completed_responses_limit: 0,
            suppliers: [],
          },
        }}
        validationSchema={validationSchema}
      >
        {() => <Form suppliers={data} projectManagers={projectManagers} />}
      </Formik>
    </MainLayout>
  );
};

export default ProjectCreateRoute;
